/* ContentEditPage — page-level editor.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────┐
 *   │ Breadcrumb  ·  Page label          [Preview] [Draft] [Publish] │
 *   ├───────────────┬────────────────────────────────────────┤
 *   │               │                                        │
 *   │  SECTIONS     │   <selected section form>              │
 *   │  (left nav)   │                                        │
 *   │               │                                        │
 *   └───────────────┴────────────────────────────────────────┘
 *
 * Sections are taken from contentSchemas.js. Clicking a section
 * on the left renders its form on the right. Sections without
 * `fields` show a "No editable fields yet" placeholder.
 *
 * Top-right actions:
 *   - Preview    : opens the live public route in a new tab
 *   - Save Draft : PUT /api/content/{key} {data, publish:false}
 *                  (public site stays on the last published version)
 *   - Publish    : PUT /api/content/{key} {data, publish:true}
 *                  (public site updates immediately)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "./api";
import { Btn, Field, TextArea, TextInput } from "./atoms";
import { MediaPicker } from "./MediaPicker";
import { SCHEMA_BY_KEY } from "./contentSchemas";
import { invalidateContent } from "../lib/useContent";
import { invalidateCmsPageRoutes } from "../lib/CmsPageRoutes";
import { slugify } from "../lib/slugify";
import {
  withSectionDefaults,
} from "./contentDefaults";

// ---------- dotted-path helpers (immutable) ----------
const getAt = (obj, path) => {
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};
const setAt = (obj, path, value) => {
  const parts = String(path).split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    const child = cur[k];
    cur[k] = Array.isArray(child) ? [...child] : { ...(child || {}) };
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
};

// ---------- Showcase slug picker ----------
// Dropdown that lets the admin pick a case-study page by its display
// name. Fetches /api/showcase?public=true once per session (cached) so
// switching the dropdown is instant. Stores the slug under the hood,
// so the live site filter (TestimonialsV5 :: slug-match) works without
// any other change.
let _showcaseListCache = null;
function ShowcaseSlugSelectField({ field, value, onChange, tid }) {
  const [items, setItems] = React.useState(_showcaseListCache || []);
  React.useEffect(() => {
    if (_showcaseListCache) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/showcase?public=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const safe = Array.isArray(list) ? list : [];
        _showcaseListCache = safe;
        setItems(safe);
      })
      .catch(() => {
        _showcaseListCache = [];
        setItems([]);
      });
  }, []);
  return (
    <Field label={field.label} hint={field.hint}>
      <select
        data-testid={`f-${tid}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="w-full bg-[#0F141A] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
      >
        <option value="">— Not assigned —</option>
        {items.map((d) => (
          <option key={d.slug} value={d.slug}>
            {d.name || d.slug}
          </option>
        ))}
      </select>
    </Field>
  );
}

// ---------- Logo list field ----------
// Editable list of { media, brand, href } items. Mirrors the Figma
// mockup layout — drag handle + thumb + label + action buttons, plus
// an "Add logo" button at the top-right of the section header.
function LogoListField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  const patch = (idx, key, val) => {
    const next = items.slice();
    next[idx] = { ...(next[idx] || {}), [key]: val };
    onChange(next);
  };
  const add = () => onChange([...items, { media: "", brand: "", href: "" }]);
  const remove = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid={`logo-list-${field.path.replace(/\./g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[0.72rem] font-bold tracking-[0.22em] uppercase text-neutral-200">
          {field.label}
          <span className="ml-2 text-neutral-500 normal-case tracking-normal font-normal">
            · {items.length} {items.length === 1 ? "logo" : "logos"}
          </span>
        </span>
        <button
          type="button"
          onClick={add}
          data-testid={`logo-list-add-${field.path.replace(/\./g, "-")}`}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-[0.68rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/40 hover:bg-[#F4D64E]/10 transition-colors"
        >
          ＋ Add logo
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/[0.10] px-4 py-6 text-center text-[0.72rem] text-neutral-500">
          No logos yet. Click <span className="text-[#F4D64E]">＋ Add logo</span> to start.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => {
            const tidBase = `${field.path.replace(/\./g, "-")}-${idx}`;
            return (
              <li
                key={idx}
                data-testid={`logo-item-${tidBase}`}
                className="flex items-center gap-3 p-3 rounded-md border border-white/[0.08] bg-[#0B0F14]"
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    aria-label="Move up"
                    className="text-neutral-500 hover:text-white text-xs leading-none w-6 h-4 flex items-center justify-center rounded disabled:opacity-30"
                    disabled={idx === 0}
                    data-testid={`logo-up-${tidBase}`}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    aria-label="Move down"
                    className="text-neutral-500 hover:text-white text-xs leading-none w-6 h-4 flex items-center justify-center rounded disabled:opacity-30"
                    disabled={idx === items.length - 1}
                    data-testid={`logo-down-${tidBase}`}
                  >
                    ▼
                  </button>
                </div>
                <span className="text-[0.62rem] font-bold tracking-[0.22em] text-neutral-500 tabular-nums w-6">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="shrink-0">
                  <MediaPicker
                    label=""
                    value={it.media || ""}
                    onChange={(url) => patch(idx, "media", url)}
                  />
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0">
                  <input
                    type="text"
                    placeholder="Brand name (alt text)"
                    value={it.brand || ""}
                    onChange={(e) => patch(idx, "brand", e.target.value)}
                    data-testid={`logo-brand-${tidBase}`}
                    className="bg-[#0F141A] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                  />
                  <input
                    type="url"
                    placeholder="Link URL (optional)"
                    value={it.href || ""}
                    onChange={(e) => patch(idx, "href", e.target.value)}
                    data-testid={`logo-href-${tidBase}`}
                    className="bg-[#0F141A] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`logo-remove-${tidBase}`}
                  className="shrink-0 text-red-300 hover:text-white border border-red-400/30 hover:border-red-400/60 bg-red-500/[0.04] px-3 py-2 rounded-md text-[0.62rem] font-bold tracking-[0.18em] uppercase transition-colors"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------- Service list field ----------
// Editable list of service entries — each is a panel that can be
// reordered (▲/▼), expanded/collapsed, removed or added. Mirrors the
// Home → Our Shopify services accordion structure 1:1.
//
// Item shape:
//   { tag_number, tag_label, title, body, highlights[], icon, media }
const SERVICE_ICON_OPTIONS = [
  { value: "Palette",           label: "Palette" },
  { value: "Wrench",            label: "Wrench" },
  { value: "Layers",            label: "Layers" },
  { value: "Code2",             label: "Code" },
  { value: "Shuffle",           label: "Shuffle" },
  { value: "Briefcase",         label: "Briefcase" },
  { value: "Rocket",            label: "Rocket" },
  { value: "Target",            label: "Target" },
  { value: "Search",            label: "Search" },
  { value: "Wallet",            label: "Wallet" },
  { value: "Zap",               label: "Zap" },
  { value: "Video",             label: "Video" },
  { value: "MousePointerClick", label: "Mouse pointer" },
  { value: "PenTool",           label: "Pen tool" },
  { value: "Film",              label: "Film" },
];

function ServiceListField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [expanded, setExpanded] = useState(0);
  // `variant="panel"` slims the +Edit drawer to title + body + icon
  // (used by simpler reorderable lists like Marketing 360 panels).
  const variant = field.variant || "service";
  // Per-field overrides win over the variant defaults — lets the
  // schema turn individual sub-fields on/off without inventing a new
  // variant for every single section.
  const showTags       = field.showTags       ?? variant !== "panel";
  const showHighlights = field.showHighlights ?? variant !== "panel";
  const showMedia      = field.showMedia      ?? variant !== "panel";
  const showIcon       = field.showIcon       ?? variant !== "panel";
  const showBody       = field.showBody       ?? true;
  const titleAsTextarea = !!field.titleAsTextarea;
  const labels = field.labels || {};
  const lTitle      = labels.title      || "Title";
  const lBody       = labels.body       || "Body paragraph";
  const lHighlights = labels.highlights || "Highlights · one per line";
  const lTagNumber  = labels.tag_number || "Tag number";
  const lTagLabel   = labels.tag_label  || "Tag label";
  const lMedia      = labels.media      || "Preview media";
  const bodyRows         = field.bodyRows         ?? 3;
  const highlightsRows   = field.highlightsRows   ?? 3;
  const highlightsPlaceholder =
    field.highlightsPlaceholder ||
    "Bespoke theme builds\nResponsive & accessible\nConversion-tuned UI";
  const mediaHint =
    field.mediaHint ||
    "Image or video, landscape (16:9). Recommended: 1600×900 px or larger.";
  const extraFields = Array.isArray(field.extraFields) ? field.extraFields : [];
  const itemNoun =
    field.itemNoun || (variant === "panel" ? "panel" : "service");
  const itemNounPlural =
    field.itemNounPlural || (variant === "panel" ? "panels" : "services");

  const patch = (idx, key, val) => {
    const next = items.slice();
    next[idx] = { ...(next[idx] || {}), [key]: val };
    onChange(next);
  };
  const add = () => {
    const seed = field.defaultItem || {
      tag_number: String(items.length + 1).padStart(2, "0"),
      tag_label: "",
      title: `New ${itemNoun}`,
      body: "",
      highlights: [],
      icon: "Code2",
      media: "",
    };
    onChange([...items, { ...seed }]);
    setExpanded(items.length);
  };
  const remove = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
    if (expanded === idx) setExpanded(null);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
    if (expanded === idx) setExpanded(j);
    else if (expanded === j) setExpanded(idx);
  };

  return (
    <div data-testid={`service-list-${field.path.replace(/\./g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[0.72rem] font-bold tracking-[0.22em] uppercase text-neutral-200">
          {field.label}
          <span className="ml-2 text-neutral-500 normal-case tracking-normal font-normal">
            · {items.length} {items.length === 1 ? itemNoun : itemNounPlural}
          </span>
        </span>
        <button
          type="button"
          onClick={add}
          data-testid={`service-list-add-${field.path.replace(/\./g, "-")}`}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-[0.68rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/40 hover:bg-[#F4D64E]/10 transition-colors"
        >
          ＋ Add {itemNoun}
        </button>
      </div>
      {field.hint ? (
        <p className="text-[0.68rem] text-neutral-500 mb-3 leading-relaxed">{field.hint}</p>
      ) : null}

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/[0.10] px-4 py-6 text-center text-[0.72rem] text-neutral-500">
          No {itemNounPlural} yet. Click <span className="text-[#F4D64E]">＋ Add {itemNoun}</span> to start.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => {
            const tidBase = `${field.path.replace(/\./g, "-")}-${idx}`;
            const isOpen = expanded === idx;
            const highlightsStr = Array.isArray(it.highlights)
              ? it.highlights.join("\n")
              : (it.highlights || "");
            return (
              <li
                key={idx}
                data-testid={`service-item-${tidBase}`}
                className="rounded-md border border-white/[0.08] bg-[#0B0F14] overflow-hidden"
              >
                {/* Header row — always visible */}
                <div className="flex items-center gap-3 p-3">
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      aria-label="Move service up"
                      title="Move up"
                      className="text-neutral-500 hover:text-white text-xs leading-none w-6 h-4 flex items-center justify-center rounded disabled:opacity-30"
                      disabled={idx === 0}
                      data-testid={`service-up-${tidBase}`}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      aria-label="Move service down"
                      title="Move down"
                      className="text-neutral-500 hover:text-white text-xs leading-none w-6 h-4 flex items-center justify-center rounded disabled:opacity-30"
                      disabled={idx === items.length - 1}
                      data-testid={`service-down-${tidBase}`}
                    >
                      ▼
                    </button>
                  </div>
                  <span className="text-[0.62rem] font-bold tracking-[0.22em] text-[#F4D64E] tabular-nums w-7">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : idx)}
                    data-testid={`service-toggle-${tidBase}`}
                    className="flex-1 flex items-center justify-between text-left text-sm font-bold text-white hover:text-[#F4D64E] transition-colors min-w-0"
                  >
                    <span className="truncate">{it.title || "Untitled service"}</span>
                    <span className="text-neutral-500 text-xs ml-3 shrink-0">
                      {isOpen ? "− Collapse" : "+ Edit"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    data-testid={`service-remove-${tidBase}`}
                    className="shrink-0 text-red-300 hover:text-white border border-red-400/30 hover:border-red-400/60 bg-red-500/[0.04] px-3 py-1.5 rounded-md text-[0.62rem] font-bold tracking-[0.18em] uppercase transition-colors"
                  >
                    Remove
                  </button>
                </div>

                {/* Expanded edit area */}
                {isOpen ? (
                  <div className="border-t border-white/[0.06] p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#0F141A]">
                    {showTags ? (
                      <>
                        <div>
                          <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                            {lTagNumber}
                          </label>
                          <input
                            type="text"
                            value={it.tag_number || ""}
                            onChange={(e) => patch(idx, "tag_number", e.target.value)}
                            placeholder="01"
                            data-testid={`service-tag-number-${tidBase}`}
                            className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#F4D64E]/60"
                          />
                        </div>
                        <div>
                          <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                            {lTagLabel}
                          </label>
                          <input
                            type="text"
                            value={it.tag_label || ""}
                            onChange={(e) => patch(idx, "tag_label", e.target.value)}
                            placeholder="Theme"
                            data-testid={`service-tag-label-${tidBase}`}
                            className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                          />
                        </div>
                      </>
                    ) : null}
                    <div className="md:col-span-2">
                      <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                        {lTitle}
                      </label>
                      {titleAsTextarea ? (
                        <textarea
                          rows={3}
                          value={it.title || ""}
                          onChange={(e) => patch(idx, "title", e.target.value)}
                          data-testid={`service-title-${tidBase}`}
                          className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 leading-[1.6]"
                        />
                      ) : (
                        <input
                          type="text"
                          value={it.title || ""}
                          onChange={(e) => patch(idx, "title", e.target.value)}
                          data-testid={`service-title-${tidBase}`}
                          className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                        />
                      )}
                    </div>
                    {showBody ? (
                      <div className="md:col-span-2">
                        <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                          {lBody}
                        </label>
                        <textarea
                          rows={bodyRows}
                          value={it.body || ""}
                          onChange={(e) => patch(idx, "body", e.target.value)}
                          data-testid={`service-body-${tidBase}`}
                          className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 leading-[1.6]"
                        />
                      </div>
                    ) : null}
                    {showHighlights ? (
                      <div className="md:col-span-2">
                        <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                          {lHighlights}
                        </label>
                        <textarea
                          rows={highlightsRows}
                          value={highlightsStr}
                          onChange={(e) => {
                            const lines = e.target.value
                              .split("\n")
                              .map((l) => l.trimEnd())
                              .filter((l, i, arr) => l !== "" || i === arr.length - 1);
                            patch(idx, "highlights", lines);
                          }}
                          placeholder={highlightsPlaceholder}
                          data-testid={`service-highlights-${tidBase}`}
                          className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 leading-[1.6] font-mono"
                        />
                      </div>
                    ) : null}
                    {showIcon ? (
                      <div>
                        <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                          Icon
                        </label>
                        <select
                          value={it.icon || "Code2"}
                          onChange={(e) => patch(idx, "icon", e.target.value)}
                          data-testid={`service-icon-${tidBase}`}
                          className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                        >
                          {SERVICE_ICON_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {showMedia ? (
                      <div className="md:col-span-2">
                        <MediaPicker
                          label={lMedia}
                          hint={mediaHint}
                          value={it.media || ""}
                          onChange={(url) => patch(idx, "media", url)}
                        />
                      </div>
                    ) : null}
                    {extraFields.map((ef) => {
                      const efTid = `service-${ef.key}-${tidBase}`;
                      const cur = it[ef.key] ?? "";
                      const colSpan =
                        ef.colSpan === 2 || ef.type === "textarea" || ef.type === "media"
                          ? "md:col-span-2"
                          : "";
                      if (ef.type === "media") {
                        return (
                          <div key={ef.key} className={colSpan}>
                            <MediaPicker
                              label={ef.label}
                              hint={ef.hint}
                              value={cur}
                              onChange={(url) => patch(idx, ef.key, url)}
                            />
                          </div>
                        );
                      }
                      if (ef.type === "showcase_slug") {
                        return (
                          <div key={ef.key} className={colSpan}>
                            <ShowcaseSlugSelectField
                              field={ef}
                              value={cur}
                              onChange={(v) => patch(idx, ef.key, v)}
                              tid={efTid}
                            />
                          </div>
                        );
                      }
                      return (
                        <div key={ef.key} className={colSpan}>
                          <label className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                            {ef.label}
                          </label>
                          {ef.type === "textarea" ? (
                            <textarea
                              rows={ef.rows ?? 3}
                              value={cur}
                              placeholder={ef.placeholder || ""}
                              onChange={(e) => patch(idx, ef.key, e.target.value)}
                              data-testid={efTid}
                              className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 leading-[1.6]"
                            />
                          ) : (
                            <input
                              type={ef.type === "url" ? "url" : "text"}
                              value={cur}
                              placeholder={ef.placeholder || ""}
                              onChange={(e) => patch(idx, ef.key, e.target.value)}
                              data-testid={efTid}
                              className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                            />
                          )}
                          {ef.hint ? (
                            <p className="mt-1.5 text-[0.65rem] text-neutral-500 leading-relaxed">
                              {ef.hint}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------- Slug / Page URL input ----------
function SlugField({ value, defaultPath, currentPath, onChange }) {
  // `value` is the **draft** slug (may differ from currentPath).
  // `currentPath` = last PUBLISHED slug.
  // `defaultPath` = original hardcoded route (e.g. "/shopify-experts-development").
  const [raw, setRaw] = useState(value || defaultPath || "");
  // Keep local state in sync if the parent resets (e.g. page switch).
  useEffect(() => {
    setRaw(value || defaultPath || "");
  }, [value, defaultPath]);

  const preview = raw ? slugify(raw) : defaultPath;
  const willChangeOnPublish = preview && preview !== currentPath;

  return (
    <div>
      <span className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
        Page URL
      </span>
      <div className="flex items-stretch gap-0 rounded-md border border-white/[0.12] bg-[#0B0F14] focus-within:border-[#F4D64E]/60 transition-colors overflow-hidden">
        <span className="inline-flex items-center px-3 text-[0.78rem] font-mono text-neutral-500 border-r border-white/[0.08] bg-white/[0.02]">
          /
        </span>
        <input
          type="text"
          value={raw.replace(/^\//, "")}
          onChange={(e) => {
            const next = e.target.value;
            setRaw(next);
            onChange(next ? slugify(next) : "");
          }}
          onBlur={() => {
            const normalised = raw ? slugify(raw) : "";
            setRaw(normalised);
          }}
          placeholder={defaultPath.replace(/^\//, "")}
          data-testid="f-page-url"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-white focus:outline-none"
        />
      </div>
      <p className="text-[0.68rem] text-neutral-500 mt-2 leading-relaxed">
        Type words or slugs — spaces and capitals are auto-cleaned. Leave empty
        to restore the original (<span className="font-mono text-neutral-400">{defaultPath}</span>).
      </p>
      <div className="mt-3 text-[0.7rem] font-mono text-neutral-300 bg-white/[0.03] border border-white/[0.06] rounded-md px-3 py-2">
        Public URL preview: <span className="text-[#F4D64E]">{preview}</span>
      </div>
      {willChangeOnPublish ? (
        <div
          data-testid="slug-change-warning"
          className="mt-3 p-3 rounded-md border border-[#F4D64E]/30 bg-[#F4D64E]/5 text-[0.7rem] text-neutral-300 leading-relaxed"
        >
          <span className="font-bold tracking-[0.12em] uppercase text-[#F4D64E] mr-2">Heads-up</span>
          On Publish, this page moves from{" "}
          <span className="font-mono text-neutral-400">{currentPath}</span> to{" "}
          <span className="font-mono text-[#F4D64E]">{preview}</span>. The old
          URL will redirect to the new one so existing links keep working.
        </div>
      ) : null}
    </div>
  );
}

// ---------- Field renderer ----------
function FieldRenderer({ field, value, onChange }) {
  const tid = field.path.replace(/\./g, "-");
  if (field.type === "textarea") {
    return (
      <Field label={field.label} hint={field.hint}>
        <TextArea
          value={value}
          onChange={onChange}
          rows={field.rows || 3}
          data-testid={`f-${tid}`}
        />
      </Field>
    );
  }
  // "media" = image OR video (preferred). "image" kept as an alias
  // so any older schema definition still renders the same picker.
  if (field.type === "media" || field.type === "image") {
    return (
      <MediaPicker
        label={field.label}
        hint={field.hint}
        value={value}
        onChange={(url) => onChange(url)}
      />
    );
  }
  if (field.type === "toggle") {
    const on = !!value;
    return (
      <div>
        <span className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
          {field.label}
        </span>
        <button
          type="button"
          onClick={() => onChange(!on)}
          data-testid={`f-${tid}`}
          className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase border transition-colors ${
            on
              ? "border-[#F4D64E]/60 text-[#F4D64E] bg-[#F4D64E]/10"
              : "border-white/[0.15] text-neutral-400 hover:text-white hover:border-white/[0.30]"
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full ${
              on ? "bg-[#F4D64E]" : "bg-neutral-600"
            }`}
          />
          {on ? "On" : "Off"}
        </button>
        {field.hint ? (
          <p className="text-[0.68rem] text-neutral-500 mt-2 leading-relaxed">
            {field.hint}
          </p>
        ) : null}
      </div>
    );
  }
  if (field.type === "select") {
    const opts = field.options || [];
    const current = value || opts[0]?.value;
    return (
      <div>
        <span className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
          {field.label}
        </span>
        <div className="flex flex-wrap gap-2" data-testid={`f-${tid}`}>
          {opts.map((opt) => {
            const active = opt.value === current;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                data-testid={`f-${tid}-${opt.value}`}
                className={`px-3.5 py-2 rounded-md text-[0.68rem] font-bold tracking-[0.18em] uppercase border transition-colors ${
                  active
                    ? "border-[#F4D64E]/60 text-[#F4D64E] bg-[#F4D64E]/10"
                    : "border-white/[0.15] text-neutral-400 hover:text-white hover:border-white/[0.30]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {field.hint ? (
          <p className="text-[0.68rem] text-neutral-500 mt-2 leading-relaxed">
            {field.hint}
          </p>
        ) : null}
      </div>
    );
  }
  if (field.type === "showcase_slug") {
    return <ShowcaseSlugSelectField field={field} value={value} onChange={onChange} tid={tid} />;
  }
  if (field.type === "logo_list") {
    return <LogoListField field={field} value={value} onChange={onChange} />;
  }
  if (field.type === "service_list") {
    return <ServiceListField field={field} value={value} onChange={onChange} />;
  }
  if (field.type === "panel_list") {
    return <ServiceListField field={{ ...field, variant: "panel" }} value={value} onChange={onChange} />;
  }
  if (field.type === "number") {
    const min = field.min ?? 0;
    const max = field.max ?? 999;
    const step = field.step ?? 1;
    const current =
      value === "" || value == null || Number.isNaN(Number(value))
        ? ""
        : Number(value);
    return (
      <Field label={field.label} hint={field.hint}>
        <div className="flex items-stretch gap-2">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={current}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") { onChange(undefined); return; }
              const n = Number(raw);
              if (Number.isNaN(n)) return;
              onChange(Math.max(min, Math.min(max, n)));
            }}
            data-testid={`f-${tid}`}
            className="w-32 bg-white/[0.04] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-[#F4D64E]/50"
          />
          {field.suffix ? (
            <span className="flex items-center text-[0.7rem] tracking-[0.15em] uppercase text-neutral-500">
              {field.suffix}
            </span>
          ) : null}
        </div>
      </Field>
    );
  }
  // text / url
  return (
    <Field label={field.label} hint={field.hint}>
      <TextInput value={value} onChange={onChange} data-testid={`f-${tid}`} />
    </Field>
  );
}

// ---------- Section Form ----------
function SectionForm({ section, data, onChange, seoData, onSeoChange, pageKey, prefilled, routePathState }) {
  if (!section) return null;
  const isSeo = section.kind === "seo";
  const activeData = isSeo ? seoData : data;
  const applyChange = isSeo ? onSeoChange : onChange;
  const isSeededDefault = !isSeo && prefilled && prefilled[section.id];

  if (!section.fields || section.fields.length === 0) {
    return (
      <div
        data-testid={`section-placeholder-${section.id}`}
        className="rounded-xl border border-white/[0.08] bg-[#0F141A] p-10 text-center"
      >
        <div className="text-[0.6rem] font-bold tracking-[0.3em] uppercase text-neutral-500 mb-3">
          Coming soon
        </div>
        <h3 className="font-display text-xl font-black tracking-[-0.02em] text-white">
          No editable fields yet
        </h3>
        <p className="mt-3 text-sm text-neutral-400 max-w-md mx-auto leading-relaxed">
          This section is locked to its current design. Text, images, links
          and other content will become editable in a future step — ask the
          team to prioritise this section when you&apos;re ready.
        </p>
      </div>
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
        {section.fields.map((f) => {
          // Special pseudo-field: page URL / slug — lives in its own state
          // slot on the parent (routePathState) because it's persisted as
          // `route_path` on the content doc, not inside the SEO doc.
          if (f.type === "slug" && routePathState) {
            return (
              <div key={f.path} className="lg:col-span-2">
                <SlugField
                  value={routePathState.draft}
                  currentPath={routePathState.current}
                  defaultPath={routePathState.default}
                  onChange={routePathState.setDraft}
                />
              </div>
            );
          }
          const v = getAt(activeData, f.path);
          const fullWidth =
            f.type === "textarea" ||
            f.type === "media" ||
            f.type === "image" ||
            f.type === "toggle" ||
            f.type === "select" ||
            f.type === "logo_list" ||
            f.type === "service_list" ||
            f.type === "panel_list";
          return (
            <div key={f.path} className={fullWidth ? "lg:col-span-2" : ""}>
              <FieldRenderer
                field={f}
                value={v}
                onChange={(val) => applyChange(setAt(activeData, f.path, val))}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

// ---------- Top action bar ----------
function TopActions({
  onPreview,
  onSaveDraft,
  onPublish,
  saving,
  publishing,
  savedDraft,
  published,
  error,
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {error ? (
        <span className="text-[0.7rem] text-red-400" data-testid="content-error">
          {error}
        </span>
      ) : null}
      {savedDraft ? (
        <span
          className="text-[0.7rem] text-neutral-400"
          data-testid="content-draft-saved"
        >
          Draft saved ✓
        </span>
      ) : null}
      {published ? (
        <span className="text-[0.7rem] text-[#F4D64E]" data-testid="content-published">
          Published ✓
        </span>
      ) : null}
      <button
        type="button"
        onClick={onPreview}
        data-testid="content-preview"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase border border-white/[0.12] text-neutral-300 hover:text-white hover:border-white/[0.30] transition-colors"
      >
        Preview ↗
      </button>
      <button
        type="button"
        onClick={onSaveDraft}
        disabled={saving || publishing}
        data-testid="content-save-draft"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase border border-white/[0.20] text-white hover:bg-white/[0.06] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? "Saving…" : "Save draft"}
      </button>
      <button
        type="button"
        onClick={onPublish}
        disabled={saving || publishing}
        data-testid="content-publish"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {publishing ? "Publishing…" : "Publish"}
      </button>
    </div>
  );
}

// ---------- Page ----------
export default function ContentEditPage() {
  const { key } = useParams();
  const schema = SCHEMA_BY_KEY[key];
  const sections = schema?.sections || [];

  const [data, setData] = useState(null);
  const [seoData, setSeoData] = useState({});
  const [prefilled, setPrefilled] = useState({});
  const [routePath, setRoutePath] = useState({
    current: schema?.route || "/",
    default: schema?.route || "/",
    draft: "",
  });
  const [hasDraft, setHasDraft] = useState(false);
  const [activeId, setActiveId] = useState(sections[0]?.id || null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [published, setPublished] = useState(false);
  const [error, setError] = useState("");

  // Load on mount (+ reset on key change)
  useEffect(() => {
    if (!schema) return;
    setActiveId(sections[0]?.id || null);
    // Pages flagged `skipSeo` (e.g. globals, case_study) have no public
    // route of their own — the SEO/route_path endpoints would 400 on a
    // null/placeholder route. We short-circuit those calls.
    const seoFetch = schema.skipSeo || !schema.route
      ? Promise.resolve({})
      : api.seoByRoute(schema.route).catch(() => ({}));
    // Load both content + SEO in parallel
    Promise.all([
      api.contentGet(key),
      seoFetch,
    ]).then(([doc, seo]) => {
      const raw = doc?.draft_data || doc?.data || {};
      const filled = withSectionDefaults(key, raw);
      setData(filled);
      // Track which sections were prefilled on THIS load so we can show
      // the "Loaded from live site — Save to persist" banner just once.
      const filledKeys = {};
      if (key === "home") {
        const rawLs = raw.logo_strip || {};
        const noLine1 = !Array.isArray(rawLs.line_1);
        const noLine2 = !Array.isArray(rawLs.line_2);
        if (noLine1 && noLine2) filledKeys.logo_strip = true;
      }
      setPrefilled(filledKeys);
      setHasDraft(!!doc?.has_draft);
      // Seed the URL slug state from the doc (falls back to defaults)
      const defaultPath = doc?.default_route_path || schema.route;
      const currentPath = doc?.route_path || defaultPath;
      const draftPath = doc?.draft_route_path || currentPath;
      setRoutePath({
        current: currentPath,
        default: defaultPath,
        draft: draftPath,
      });
      // Seed SEO form state
      const seoDoc = seo || {};
      const seoPublished = {
        title: seoDoc.title || "",
        description: seoDoc.description || "",
        og_title: seoDoc.og_title || "",
        og_description: seoDoc.og_description || "",
        og_image: seoDoc.og_image || "",
        canonical: seoDoc.canonical || "",
        noindex: !!seoDoc.noindex,
      };
      const seoDraft = seoDoc.draft || null;
      setSeoData({ ...seoPublished, ...(seoDraft || {}) });
      if (seoDoc.has_draft) setHasDraft(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const active = useMemo(
    () => sections.find((s) => s.id === activeId) || sections[0],
    [sections, activeId]
  );

  if (!schema) {
    return (
      <div className="text-sm text-neutral-500" data-testid="content-unknown">
        Unknown content key: {key}
      </div>
    );
  }
  if (data === null) return <div className="text-sm text-neutral-500">Loading…</div>;

  const flashOk = (setter) => {
    setter(true);
    setTimeout(() => setter(false), 3500);
  };

  const runSave = async (publish) => {
    setError("");
    if (publish) setPublishing(true);
    else setSaving(true);
    try {
      // Build the content PUT body — include route_path only if the user
      // actually edited the slug (non-empty draft that differs from
      // current). Pages flagged `skipRouteSlug` (globals, case_study)
      // have no public route, so we never send route_path for them.
      const contentBody = { data, publish };
      if (
        !schema.skipRouteSlug &&
        routePath.draft !== undefined &&
        routePath.draft !== null
      ) {
        contentBody.route_path = routePath.draft;
      }
      // SEO is also skipped for routeless schemas (no canonical URL =
      // no SEO doc).
      const seoCall = schema.skipSeo
        ? Promise.resolve(null)
        : api.seoUpdate({ route: schema.route, ...seoData }, publish);
      const [contentRes] = await Promise.all([
        api.contentPut(key, contentBody),
        seoCall,
      ]);
      invalidateContent(key);
      invalidateCmsPageRoutes();
      // Refresh routePath state from server response so the form shows
      // the canonical persisted value (e.g. slugified version).
      if (contentRes) {
        const newCurrent = contentRes.route_path || routePath.current;
        const newDraft = contentRes.draft_route_path || newCurrent;
        setRoutePath((p) => ({ ...p, current: newCurrent, draft: newDraft }));
        // If Publish moved the page to a new path, navigate the Preview
        // link and notify the user. We intentionally do NOT redirect the
        // admin tab — the admin lives at /admin/content/:key and the URL
        // rename only affects the public page.
      }
      if (publish) {
        flashOk(setPublished);
        setHasDraft(false);
      } else {
        flashOk(setSavedDraft);
        setHasDraft(true);
      }
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      if (publish) setPublishing(false);
      else setSaving(false);
    }
  };

  const onSaveDraft = () => runSave(false);
  const onPublish = () => runSave(true);
  const onPreview = () => {
    // Preview always opens the LIVE (published) URL.
    const target = routePath.current || schema.route;
    if (target) window.open(target, "_blank", "noopener");
  };

  return (
    <div data-testid={`admin-content-edit-${key}`} className="pb-20">
      {/* =========== Top header + actions =========== */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
        <div>
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
            <Link to="/admin/content" className="hover:text-white" data-testid="content-back">
              ← Pages
            </Link>
          </div>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em] text-white">
            {schema.label}
          </h1>
          <div className="mt-1 text-[0.7rem] tracking-[0.18em] uppercase text-neutral-500 font-mono" data-testid="content-current-path">
            {routePath.current}
            {routePath.current !== routePath.default ? (
              <span className="ml-2 text-[0.6rem] text-neutral-600 normal-case tracking-normal">
                (was {routePath.default})
              </span>
            ) : null}
          </div>
          {hasDraft ? (
            <div
              data-testid="content-has-draft-pill"
              className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/[0.10] text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E]" />
              Unpublished draft
            </div>
          ) : null}
        </div>
        <TopActions
          onPreview={onPreview}
          onSaveDraft={onSaveDraft}
          onPublish={onPublish}
          saving={saving}
          publishing={publishing}
          savedDraft={savedDraft}
          published={published}
          error={error}
        />
      </div>

      {/* =========== 2-col body =========== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-8">
        {/* LEFT — section navigator */}
        <aside
          data-testid="sections-nav"
          className="lg:col-span-4 xl:col-span-3"
        >
          <div className="sticky top-6">
            <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500 mb-4">
              Page sections
            </div>
            <ul className="space-y-1.5">
              {sections.map((s, idx) => {
                const isActive = s.id === active?.id;
                const editable = Boolean(s.fields && s.fields.length);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(s.id)}
                      data-testid={`section-nav-${s.id}`}
                      className={`group w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-md transition-colors ${
                        isActive
                          ? "bg-[#F4D64E]/10 border border-[#F4D64E]/30"
                          : "border border-transparent hover:bg-white/[0.03]"
                      }`}
                    >
                      <span
                        className={`text-[0.6rem] font-bold tracking-[0.22em] tabular-nums shrink-0 mt-0.5 ${
                          isActive ? "text-[#F4D64E]" : "text-neutral-500"
                        }`}
                      >
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block text-[0.82rem] font-bold tracking-[-0.005em] ${
                            isActive ? "text-white" : "text-neutral-200 group-hover:text-white"
                          }`}
                        >
                          {s.label}
                        </span>
                        {!editable ? (
                          <span className="block text-[0.6rem] tracking-[0.2em] uppercase text-neutral-600 mt-1">
                            Not editable yet
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* RIGHT — active section form */}
        <section className="lg:col-span-8 xl:col-span-9">
          {active ? (
            <div data-testid={`section-panel-${active.id}`}>
              <div className="mb-7">
                <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
                  Section · {String(sections.indexOf(active) + 1).padStart(2, "0")}
                </div>
                <h2 className="mt-3 font-display text-2xl font-black tracking-[-0.02em] text-white">
                  {active.label}
                </h2>
                {active.description ? (
                  <p className="mt-2 text-sm text-neutral-400 leading-relaxed max-w-2xl">
                    {active.description}
                  </p>
                ) : null}
                {prefilled && prefilled[active.id] ? (
                  <div
                    data-testid="section-seeded-banner"
                    className="mt-4 p-3 rounded-md bg-[#F4D64E]/5 border border-[#F4D64E]/30 text-[0.72rem] text-neutral-300 leading-relaxed max-w-2xl"
                  >
                    <span className="font-bold tracking-[0.12em] uppercase text-[#F4D64E] mr-2">
                      Loaded from live site
                    </span>
                    The values below mirror what is currently on the public
                    page. Edit, remove, or add items — then click{" "}
                    <span className="text-white font-bold">Save draft</span> or{" "}
                    <span className="text-[#F4D64E] font-bold">Publish</span>{" "}
                    to make them your own.
                  </div>
                ) : null}
              </div>

              <SectionForm
                section={active}
                data={data}
                onChange={setData}
                seoData={seoData}
                onSeoChange={setSeoData}
                pageKey={key}
                prefilled={prefilled}
                routePathState={{
                  current: routePath.current,
                  default: routePath.default,
                  draft: routePath.draft,
                  setDraft: (d) => setRoutePath((p) => ({ ...p, draft: d })),
                }}
              />
            </div>
          ) : null}
        </section>
      </div>

      {/* =========== Sticky bottom bar — mirrors top actions on long pages =========== */}
      <div className="mt-12 flex items-center gap-3 flex-wrap sticky bottom-0 bg-[#0B0F14]/95 backdrop-blur-sm py-4 -mx-4 px-4 border-t border-white/[0.06]">
        <TopActions
          onPreview={onPreview}
          onSaveDraft={onSaveDraft}
          onPublish={onPublish}
          saving={saving}
          publishing={publishing}
          savedDraft={savedDraft}
          published={published}
          error={error}
        />
      </div>
    </div>
  );
}
